import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';

/*
 * Student billing — bills are in HOURS; rupee amounts live only on payments.
 *   monthly   → billed AFTER, for hours used (full month / this month so far)
 *   recurring → billed BEFORE, for the next hours; classes count down the bills
 * Backend: app/routes/student_bills.py
 */

// Monthly billing counts classes from here (fresh start). Keep in sync with BILLING_START in the backend.
const BILLING_START_YM = '2026-10';

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x.msg).join('; ');
  return d || fallback;
};
const fmtH = h => `${Number(h || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}h`;
const fmtDate = s => (s ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const monthName = ym => { const [y, m] = ym.split('-'); return new Date(+y, +m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }); };
const localISO = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const rupees = n => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Months that can be billed as "Full month", newest first. The current month is
// included — it isn't finished, so its hours are entered manually.
function billableMonths() {
  const out = [];
  const now = new Date();
  let d = new Date(now.getFullYear(), now.getMonth(), 1);
  const [sy, sm] = BILLING_START_YM.split('-').map(Number);
  const start = new Date(sy, sm - 1, 1);
  while (d >= start && out.length < 24) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  }
  return out;
}

const PAY = {
  unpaid:  { bg: '#fee2e2', fg: '#b91c1c', label: 'Unpaid' },
  partial: { bg: '#fef3c7', fg: '#92400e', label: 'Partial' },
  paid:    { bg: '#dcfce7', fg: '#15803d', label: 'Paid' },
};

function PayBadge({ bill }) {
  if (bill.status === 'cancelled') {
    return <span style={pill('#f1f5f9', '#64748b')}>Cancelled</span>;
  }
  const p = PAY[bill.pay_status] || PAY.unpaid;
  return (
    <span style={pill(p.bg, p.fg)}>
      {p.label}{bill.pay_status === 'partial' ? ` · ${fmtH(bill.pending_hours)} pending` : ''}
    </span>
  );
}

function pill(bg, fg) {
  return { display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: bg, color: fg, whiteSpace: 'nowrap' };
}

function Modal({ title, onClose, children, width = 560, z = 3000 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.5)', zIndex: z, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: '#0f1f45' }}>{title}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Notice({ text, onClose }) {
  if (!text) return null;
  const ok = text.startsWith('✓');
  return (
    <div onClick={onClose} style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 12, cursor: 'pointer',
      background: ok ? '#f0fdf4' : '#fef2f2', color: ok ? '#15803d' : '#b91c1c', border: `1px solid ${ok ? '#86efac' : '#fca5a5'}` }}>
      {text}
    </div>
  );
}

// Invoice number with the Indian financial-year prefix used elsewhere (SAP/2026-27/…).
function InvoiceInput({ value, onChange }) {
  const d = new Date();
  const fy = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  const prefix = `SAP/${fy}-${String(fy + 1).slice(-2)}/`;
  const suffix = (value || '').replace(/^SAP\/\d{4}-\d{2}\//, '');
  return (
    <div style={{ display: 'flex', border: '1.5px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <span style={{ padding: '9px 12px', background: '#f8faff', color: '#475569', fontSize: 13, fontWeight: 700, borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{prefix}</span>
      <input type="text" placeholder="e.g. 24 (optional)" value={suffix}
        onChange={e => { const c = e.target.value.replace(/^SAP\/\d{4}-\d{2}\//, ''); onChange(c ? prefix + c : ''); }}
        style={{ flex: 1, padding: '9px 12px', border: 'none', fontSize: 13, outline: 'none', minWidth: 0 }} />
    </div>
  );
}

// ── Generate bill ───────────────────────────────────────────────────────────
function GenerateBillModal({ row, onClose, onDone }) {
  const monthly = row.billing_type === 'monthly';
  const months = billableMonths();
  const thisMonth = localISO().slice(0, 7);
  const monthEnd = (() => { const d = new Date(); return localISO(new Date(d.getFullYear(), d.getMonth() + 1, 0)); })();
  const [mode, setMode] = useState(monthly ? (row.ready_to_bill_month ? 'full_month' : 'to_date') : 'recurring');
  const [month, setMonth] = useState(row.ready_to_bill_month || months[0] || '');
  const [endDate, setEndDate] = useState(localISO());
  const [manual, setManual] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [hours, setHours] = useState(row.current_bill?.hours || '');
  const [startDate, setStartDate] = useState(localISO());
  const [invoice, setInvoice] = useState('');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // Monthly bills: show the counted hours BEFORE saving.
  useEffect(() => {
    if (!monthly) return;
    if ((mode === 'full_month' && !month) || (mode === 'upto_date' && !endDate)) { setPreview(null); return; }
    let live = true;
    setPreview({ loading: true });
    API.post('/bills/generate', {
      student_id: row.student_id, mode, preview: true,
      month: mode === 'full_month' ? month : null,
      end_date: mode === 'upto_date' ? endDate : null,
    })
      .then(r => live && setPreview(r.data))
      .catch(e => live && setPreview({ error: errMsg(e, 'Could not calculate') }));
    return () => { live = false; };
  }, [monthly, mode, month, endDate, row.student_id]);

  // A period running past today (e.g. the whole current month) can only be billed with manual hours.
  const mustManual = !!preview?.needs_manual;
  const useManual = manual || mustManual;
  const manualOk = parseFloat(manualHours) > 0;
  const canCreate = !monthly || (preview && !preview.loading && !preview.error && (useManual ? manualOk : preview.hours > 0));

  const submit = async () => {
    if (!monthly && !(parseFloat(hours) > 0)) { setMsg('Enter the number of hours for this bill'); return; }
    if (monthly && useManual && !manualOk) { setMsg('Enter the hours to bill'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await API.post('/bills/generate', {
        student_id: row.student_id, mode,
        month: mode === 'full_month' ? month : null,
        end_date: mode === 'upto_date' ? endDate : null,
        manual_hours: monthly && useManual ? parseFloat(manualHours) : null,
        hours: monthly ? null : parseFloat(hours),
        start_date: !monthly && row.needs_first_bill ? startDate : null,
        invoice_number: invoice || null, notes: notes || null,
      });
      onDone(`✓ Bill created for ${row.student_name}: ${r.data.label} — ${fmtH(r.data.hours)}`);
    } catch (e) { setMsg(errMsg(e, 'Could not create the bill')); }
    finally { setBusy(false); }
  };

  const radio = (value, label, sub, disabled) => (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      border: `1.5px solid ${mode === value ? '#2554b8' : '#e2e8f0'}`, background: mode === value ? '#f0f5ff' : '#fff' }}>
      <input type="radio" checked={mode === value} disabled={disabled} onChange={() => setMode(value)} style={{ marginTop: 3 }} />
      <div><div style={{ fontWeight: 700, fontSize: 14 }}>{label}</div><div style={{ fontSize: 12, color: '#64748b' }}>{sub}</div></div>
    </label>
  );

  return (
    <Modal title={`🧾 ${monthly ? 'Generate bill' : 'Bill next hours'} — ${row.student_name}`} onClose={onClose}>
      <Notice text={msg} onClose={() => setMsg('')} />
      {monthly ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {radio('full_month', 'Full month', 'A whole month — this month too, with hours entered manually')}
            {mode === 'full_month' && (
              <select className="form-control" value={month} onChange={e => setMonth(e.target.value)} style={{ marginLeft: 30, width: 'calc(100% - 30px)' }}>
                {months.map(m => <option key={m} value={m}>{monthName(m)}{m === thisMonth ? ' (not finished — enter hours manually)' : ''}</option>)}
              </select>
            )}
            {radio('to_date', 'This month so far', `From ${fmtDate(row.next_bill_from)} up to today`)}
            {radio('upto_date', 'Up to a date', `From ${fmtDate(row.next_bill_from)} up to the date you pick`)}
            {mode === 'upto_date' && (
              <input className="form-control" type="date" value={endDate} min={row.next_bill_from} max={monthEnd}
                onChange={e => setEndDate(e.target.value)} style={{ marginLeft: 30, width: 'calc(100% - 30px)' }} />
            )}
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: '#f8faff', border: '1px solid #dbeafe', marginBottom: 14, fontSize: 13 }}>
            {!preview ? <span style={{ color: '#94a3b8' }}>Pick a period</span>
              : preview.loading ? <span style={{ color: '#94a3b8' }}>Calculating…</span>
              : preview.error ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>{preview.error}</span>
              : <>
                  <div style={{ color: '#64748b' }}>{fmtDate(preview.period_start)} → {fmtDate(preview.period_end)}</div>
                  {useManual ? (
                    <div style={{ marginTop: 2 }}>
                      <span style={{ fontWeight: 800, fontSize: 20, color: '#1e3a8a' }}>{manualOk ? fmtH(manualHours) : '—'}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}> will be billed (entered manually) · classes counted so far: {fmtH(preview.counted_hours)}</span>
                    </div>
                  ) : preview.hours > 0 ? (
                    <div style={{ fontWeight: 800, fontSize: 20, color: '#1e3a8a', marginTop: 2 }}>{fmtH(preview.hours)} <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>used — this is what will be billed</span></div>
                  ) : (
                    <div style={{ color: '#92400e', fontWeight: 600, marginTop: 2 }}>No completed classes in this period. Tick "Enter hours manually" to bill anyway.</div>
                  )}
                </>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, cursor: mustManual ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={useManual} disabled={mustManual} onChange={e => setManual(e.target.checked)} />
              Enter hours manually
            </label>
            {useManual && (
              <input className="form-control" type="number" min="0.5" step="0.5" placeholder="Hours" value={manualHours}
                onChange={e => setManualHours(e.target.value)} style={{ width: 120 }} />
            )}
            {mustManual && <span style={{ fontSize: 12, color: '#64748b' }}>Needed — this period runs past today.</span>}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
            {row.needs_first_bill ? 'First bill for this student. Classes from the start date below are counted against it.'
              : row.ready_for_next_bill ? `All paid hours are used${row.over_hours > 0 ? ` (${fmtH(row.over_hours)} over — those come off this bill)` : ''}.`
              : `${fmtH(row.current_bill?.remaining)} still left on the current bill — this one starts counting after that.`}
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Hours for this bill</label>
              <input className="form-control" type="number" min="0.5" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. 20" />
            </div>
            {row.needs_first_bill && (
              <div className="form-group">
                <label>Count classes from</label>
                <input className="form-control" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
            )}
          </div>
        </>
      )}
      <div className="form-group">
        <label>Invoice number</label>
        <InvoiceInput value={invoice} onChange={setInvoice} />
      </div>
      <div className="form-group">
        <label>Notes (optional, admin only)</label>
        <input className="form-control" value={notes} maxLength={500} onChange={e => setNotes(e.target.value)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy || !canCreate}>
          {busy ? 'Saving…' : 'Create bill'}
        </button>
      </div>
    </Modal>
  );
}

// ── Record payment ──────────────────────────────────────────────────────────
function RecordPaymentModal({ bill, studentName, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [hours, setHours] = useState(bill.pending_hours);
  const [date, setDate] = useState(localISO());
  const [notes, setNotes] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkable, setLinkable] = useState([]);
  const partial = parseFloat(hours) > 0 && parseFloat(hours) < bill.pending_hours;

  useEffect(() => {
    API.get(`/bills/${bill.id}/linkable-payments`).then(r => setLinkable(r.data || [])).catch(() => setLinkable([]));
  }, [bill.id]);

  const link = async p => {
    const covers = p.hours != null ? `${fmtH(p.hours)}` : `the pending ${fmtH(bill.pending_hours)}`;
    if (!window.confirm(`Link the ${rupees(p.amount)} payment of ${fmtDate(p.payment_date)} to this bill?
It will count for ${covers}.`)) return;
    setBusy(true); setMsg('');
    try {
      const r = await API.post(`/bills/${bill.id}/link-payment`, { payment_id: p.id });
      onDone(r.data.pay_status === 'paid'
        ? `✓ Payment linked — ${studentName}'s bill is fully paid`
        : `✓ Payment linked — ${fmtH(r.data.pending_hours)} still pending on this bill`);
    } catch (e) { setMsg(errMsg(e, 'Could not link the payment')); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!(parseFloat(amount) > 0)) { setMsg('Enter the amount the parent paid'); return; }
    if (!(parseFloat(hours) > 0)) { setMsg('Enter the hours this payment covers'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await API.post(`/bills/${bill.id}/payments`, { amount: parseFloat(amount), hours: parseFloat(hours), payment_date: date, notes: notes || null });
      onDone(r.data.pay_status === 'paid'
        ? `✓ Payment recorded — ${studentName}'s bill is fully paid`
        : `✓ Payment recorded — ${fmtH(r.data.pending_hours)} still pending on this bill`);
    } catch (e) { setMsg(errMsg(e, 'Could not record the payment')); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`💳 Record payment — ${studentName}`} onClose={onClose} z={3100} width={480}>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
        {bill.label} · {fmtH(bill.hours)} · <b style={{ color: '#92400e' }}>{fmtH(bill.pending_hours)} pending</b>
      </div>
      <Notice text={msg} onClose={() => setMsg('')} />
      {linkable.length > 0 && (
        <div style={{ padding: 12, borderRadius: 10, background: '#f8faff', border: '1px solid #dbeafe', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Already recorded this payment?</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Link it instead of entering it again, so the parent doesn't see it twice.</div>
          {linkable.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid #e2e8f0', fontSize: 13 }}>
              <span>{fmtDate(p.payment_date)} — <b>{rupees(p.amount)}</b>{p.hours != null ? ` · ${fmtH(p.hours)}` : ''}{p.invoice_number ? ` · ${p.invoice_number}` : ''}</span>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} disabled={busy} onClick={() => link(p)}>🔗 Link</button>
            </div>
          ))}
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Or record a new payment below.</div>
        </div>
      )}
      <div className="grid-2">
        <div className="form-group">
          <label>Amount paid (₹)</label>
          <input className="form-control" type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Hours covered</label>
          <input className="form-control" type="number" min="0.5" step="0.5" max={bill.pending_hours} value={hours} onChange={e => setHours(e.target.value)} />
        </div>
      </div>
      {partial && (
        <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginTop: -6, marginBottom: 12 }}>
          Partial payment — {fmtH(bill.pending_hours - parseFloat(hours))} will stay pending.
        </div>
      )}
      <div className="grid-2">
        <div className="form-group">
          <label>Payment date</label>
          <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Note (optional)</label>
          <input className="form-control" value={notes} maxLength={500} placeholder="e.g. UPI ref" onChange={e => setNotes(e.target.value)} />
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 12 }}>The parent can see this payment and amount in their app.</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save payment'}</button>
      </div>
    </Modal>
  );
}

// ── Revise bill ─────────────────────────────────────────────────────────────
function ReviseModal({ bill, studentName, onClose, onDone }) {
  const [hours, setHours] = useState(bill.hours);
  const [invoice, setInvoice] = useState(bill.invoice_number || '');
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const monthly = bill.billing_type === 'monthly';

  const recalc = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await API.post(`/bills/${bill.id}/recalculate`, { reason: reason || null });
      if (r.data.changed) onDone(`✓ ${studentName}: ${r.data.message}`);
      else setMsg(`✓ ${r.data.message}`);
    } catch (e) { setMsg(errMsg(e, 'Could not recalculate')); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (reason.trim().length < 3) { setMsg('Please give a reason — it is kept in the bill history'); return; }
    setBusy(true); setMsg('');
    try {
      await API.patch(`/bills/${bill.id}`, { hours: parseFloat(hours), invoice_number: invoice, reason: reason.trim() });
      onDone(`✓ Bill revised for ${studentName}`);
    } catch (e) { setMsg(errMsg(e, 'Could not revise the bill')); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`✏️ Revise bill — ${studentName}`} onClose={onClose} z={3100} width={500}>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
        {bill.label} · currently {fmtH(bill.hours)}{bill.paid_hours > 0 && !bill.is_opening ? ` · ${fmtH(bill.paid_hours)} already paid` : ''}
      </div>
      <Notice text={msg} onClose={() => setMsg('')} />
      {monthly && (
        <div style={{ padding: 14, borderRadius: 10, background: '#f8faff', border: '1px solid #dbeafe', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>🔄 Recalculate from class logs</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Use this after correcting a class log (e.g. a class wrongly marked completed). Recounts the same period.</div>
          <button className="btn btn-secondary btn-sm" onClick={recalc} disabled={busy}>Recalculate</button>
        </div>
      )}
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{monthly ? 'Or adjust manually' : 'Adjust'}</div>
      <div className="grid-2">
        <div className="form-group">
          <label>Hours</label>
          <input className="form-control" type="number" min="0.5" step="0.5" value={hours} onChange={e => setHours(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Invoice number</label>
          <input className="form-control" value={invoice} maxLength={60} onChange={e => setInvoice(e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label>Reason (required — kept in history)</label>
        <input className="form-control" value={reason} maxLength={500} placeholder="e.g. Parent request: 2 classes not attended" onChange={e => setReason(e.target.value)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save revision'}</button>
      </div>
    </Modal>
  );
}

// ── Classes behind a bill ───────────────────────────────────────────────────
function ClassesModal({ bill, studentName, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    API.get(`/bills/${bill.id}/classes`).then(r => setData(r.data)).catch(e => setErr(errMsg(e, 'Could not load classes')));
  }, [bill.id]);
  return (
    <Modal title={`📚 Classes — ${studentName}`} onClose={onClose} z={3100} width={720}>
      {err && <Notice text={err} />}
      {!data ? !err && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Loading…</div> : (
        <>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
            {fmtDate(data.from)} → {fmtDate(data.to)} · <b style={{ color: '#0f1f45' }}>{fmtH(data.total_hours)}</b> across {data.classes.length} class{data.classes.length === 1 ? '' : 'es'}
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Time</th><th>Subject</th><th>Companion</th><th>Mode</th><th>Hours</th></tr></thead>
              <tbody>
                {data.classes.length === 0
                  ? <tr><td colSpan="6" style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>No classes in this period</td></tr>
                  : data.classes.map((c, i) => (
                    <tr key={i}>
                      <td className="mono text-small">{fmtDate(c.date)}</td>
                      <td className="text-small">{c.time}</td>
                      <td>{c.subject}</td>
                      <td className="text-small">{c.tutor}</td>
                      <td className="text-small">{c.mode === 'offline' ? '🏫 Offline' : '🌐 Online'}</td>
                      <td style={{ fontWeight: 700 }}>{fmtH(c.hours)}{c.kind === 'student_absent' && <span style={{ ...pill('#fef3c7', '#92400e'), marginLeft: 6 }}>Absent</span>}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

// Shared row of actions + nested modals for one bill.
function useBillActions(onChanged) {
  const [payFor, setPayFor] = useState(null);
  const [reviseFor, setReviseFor] = useState(null);
  const [classesFor, setClassesFor] = useState(null);
  const [notice, setNotice] = useState('');

  const done = text => { setPayFor(null); setReviseFor(null); setNotice(text); onChanged && onChanged(); };
  const cancelBill = async (bill, studentName) => {
    const reason = window.prompt(`Cancel this bill for ${studentName}?\n${bill.label} · ${fmtH(bill.hours)}\n\nReason (optional):`);
    if (reason === null) return;
    try {
      await API.post(`/bills/${bill.id}/cancel`, { reason: reason || null });
      done(`✓ Bill cancelled for ${studentName}`);
    } catch (e) { setNotice(errMsg(e, 'Could not cancel the bill')); }
  };

  const buttons = (bill, studentName) => bill.status !== 'active' ? null : (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {bill.pending_hours > 0 && (
        <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={() => setPayFor({ bill, studentName })}>💳 Record payment</button>
      )}
      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setReviseFor({ bill, studentName })}>✏️ Revise</button>
      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setClassesFor({ bill, studentName })}>📚 Classes</button>
      {!bill.is_opening && (bill.payments || []).length === 0 && (
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#dc2626' }} onClick={() => cancelBill(bill, studentName)}>Cancel bill</button>
      )}
    </div>
  );

  const modals = (
    <>
      {payFor && <RecordPaymentModal {...payFor} onClose={() => setPayFor(null)} onDone={done} />}
      {reviseFor && <ReviseModal {...reviseFor} onClose={() => setReviseFor(null)} onDone={done} />}
      {classesFor && <ClassesModal {...classesFor} onClose={() => setClassesFor(null)} />}
    </>
  );
  return { buttons, modals, notice, setNotice };
}

// ── One student's bills ─────────────────────────────────────────────────────
function StudentBillsModal({ studentId, studentName, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [openHist, setOpenHist] = useState(null);
  const load = useCallback(() => {
    API.get(`/bills/student/${studentId}`).then(r => setData(r.data)).catch(e => setErr(errMsg(e, 'Could not load bills')));
  }, [studentId]);
  useEffect(() => { load(); }, [load]);
  const { buttons, modals, notice, setNotice } = useBillActions(() => { load(); onChanged && onChanged(); });
  const unlink = async (b, p) => {
    if (!window.confirm(`Unlink the ${rupees(p.amount)} payment of ${fmtDate(p.payment_date)} from "${b.label}"?
The payment stays in Student Payments; this bill goes back to pending.`)) return;
    try {
      await API.delete(`/bills/${b.id}/payments/${p.id}`);
      setNotice('✓ Payment unlinked');
      load(); onChanged && onChanged();
    } catch (e) { setNotice(errMsg(e, 'Could not unlink the payment')); }
  };

  return (
    <Modal title={`📂 Bills — ${studentName}`} onClose={onClose} width={760}>
      <Notice text={notice || err} onClose={() => { setNotice(''); setErr(''); }} />
      {!data ? !err && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Loading…</div> : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Stat label="Billing" value={data.summary.billing_type === 'monthly' ? 'Monthly' : 'Recurring'} />
            <Stat label="This month" value={fmtH(data.summary.this_month_hours)} />
            {data.summary.billing_type === 'monthly'
              ? <Stat label="Not billed yet" value={fmtH(data.summary.unbilled_hours)} />
              : <Stat label="Paid hours left" value={fmtH(data.summary.remaining_hours)} />}
            <Stat label="Payment pending" value={fmtH(data.summary.pending_hours)} warn={data.summary.pending_hours > 0} />
          </div>
          {data.bills.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🧾</div><p>No bills yet.</p></div>
          ) : data.bills.map(b => (
            <div key={b.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', marginBottom: 10, opacity: b.status === 'cancelled' ? 0.55 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f1f45' }}>{b.label} <span style={{ fontWeight: 700, color: '#2554b8' }}>· {fmtH(b.hours)}</span></div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {b.billing_type === 'monthly'
                      ? `${fmtDate(b.period_start)} → ${fmtDate(b.period_end)}`
                      : `Counting from ${fmtDate(b.first_used_on || b.period_start)}${b.used_up_on ? ` · used up ${fmtDate(b.used_up_on)}` : b.used_hours != null ? ` · ${fmtH(b.used_hours)} used so far` : ''}`}
                    {b.invoice_number ? ` · ${b.invoice_number}` : ''}
                  </div>
                </div>
                <PayBadge bill={b} />
              </div>
              {b.payments.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12.5, color: '#334155' }}>
                  {b.payments.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>💳 {fmtDate(p.payment_date)} — <b>{rupees(p.amount)}</b>{p.hours != null ? ` for ${fmtH(p.hours)}` : ''}{p.notes ? ` · ${p.notes}` : ''}</span>
                      {b.status === 'active' && (
                        <button onClick={() => unlink(b, p)} title="Detach from this bill — the payment stays in Student Payments"
                          style={{ border: 'none', background: 'none', color: '#94a3b8', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>unlink</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                {buttons(b, studentName) || <span />}
                {b.revisions.length > 0 && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#7c3aed' }} onClick={() => setOpenHist(openHist === b.id ? null : b.id)}>
                    🕘 History ({b.revisions.length})
                  </button>
                )}
              </div>
              {openHist === b.id && (
                <div style={{ marginTop: 8, borderTop: '1px dashed #e2e8f0', paddingTop: 8, fontSize: 12, color: '#475569' }}>
                  {b.revisions.map(r => (
                    <div key={r.revision} style={{ marginBottom: 4 }}>
                      <b>v{r.revision}</b>: {fmtH(r.hours)}{r.invoice_number ? ` · ${r.invoice_number}` : ''} — changed by {r.changed_by}{r.changed_at ? ` on ${new Date(r.changed_at + 'Z').toLocaleString('en-GB')}` : ''}. <i>Reason: {r.reason}</i>
                    </div>
                  ))}
                  <div><b>v{b.revision}</b> (current): {fmtH(b.hours)}{b.invoice_number ? ` · ${b.invoice_number}` : ''}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {modals}
    </Modal>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div style={{ padding: '8px 14px', borderRadius: 10, background: warn ? '#fffbeb' : '#f8faff', border: `1px solid ${warn ? '#fde68a' : '#e2e8f0'}`, minWidth: 110 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: warn ? '#92400e' : '#0f1f45' }}>{value}</div>
    </div>
  );
}

// ── WhatsApp reminder (existing /payments/send-reminder template) ───────────
function ReminderModal({ row, onClose }) {
  const [amount, setAmount] = useState('');
  const [due, setDue] = useState('');
  const [msg, setMsg] = useState('');
  const send = async () => {
    if (!amount.trim() || !due) { setMsg('Please fill in both fields.'); return; }
    const [y, m, d] = due.split('-');
    try {
      await API.post('/payments/send-reminder', { student_id: row.student_id, amount: amount.trim(), due_date: `${d}-${m}-${y}` });
      setMsg('✓ Reminder sent');
      setTimeout(onClose, 1200);
    } catch (e) { setMsg(errMsg(e, 'Failed to send reminder.')); }
  };
  return (
    <Modal title={`📲 Payment reminder — ${row.student_name}`} onClose={onClose} width={420}>
      {row.pending_hours > 0 && <div style={{ fontSize: 13, color: '#92400e', marginBottom: 12 }}>{fmtH(row.pending_hours)} payment pending on this student's bills.</div>}
      <Notice text={msg} onClose={() => setMsg('')} />
      <div className="form-group">
        <label>Amount due</label>
        <input className="form-control" placeholder="e.g. ₹5000" value={amount} onChange={e => setAmount(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Due date</label>
        <input className="form-control" type="date" value={due} onChange={e => setDue(e.target.value)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={send}>Send WhatsApp</button>
      </div>
    </Modal>
  );
}

// ── Tab: Student Hours (overview) ───────────────────────────────────────────
export function StudentBillingOverview() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [genFor, setGenFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [remindFor, setRemindFor] = useState(null);
  const [popupOn, setPopupOn] = useState(null);

  const load = useCallback(() => {
    API.get('/bills/overview').then(r => { setRows(r.data || []); setErr(''); }).catch(e => setErr(errMsg(e, 'Could not load students')));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    API.get('/bills/popup-setting').then(r => setPopupOn(!!r.data.enabled)).catch(() => {});
  }, []);

  const togglePopupAll = async () => {
    const next = !popupOn;
    if (!window.confirm(next ? 'Turn the fee reminder popup ON for parents with an unpaid bill?\n(Students you muted individually stay muted.)' : 'Turn the fee reminder popup OFF for ALL parents?')) return;
    try {
      await API.patch('/bills/popup-setting', { enabled: next });
      setPopupOn(next);
      setNotice(next ? '✓ Fee popup is ON' : '✓ Fee popup is OFF for all parents');
    } catch (e) { setNotice(errMsg(e, 'Could not change the popup setting')); }
  };
  const togglePopupOne = async r => {
    try {
      await API.post(`/payments/student-hours/${r.student_id}/${r.popup_muted ? 'unmute' : 'mute'}-payment-popup`);
      setNotice(`✓ Fee popup ${r.popup_muted ? 'unmuted' : 'muted'} for ${r.student_name}`);
      load();
    } catch (e) { setNotice(errMsg(e, 'Could not change the popup for this student')); }
  };

  const toBill = r => (r.billing_type === 'monthly' ? !!r.ready_to_bill_month : !!r.ready_for_next_bill);
  const all = rows || [];
  const counts = {
    all: all.length,
    monthly: all.filter(r => r.billing_type === 'monthly').length,
    recurring: all.filter(r => r.billing_type === 'recurring').length,
    to_bill: all.filter(toBill).length,
    pending: all.filter(r => r.pending_hours > 0).length,
  };
  const shown = all
    .filter(r => filter === 'all' || (filter === 'to_bill' ? toBill(r) : filter === 'pending' ? r.pending_hours > 0 : r.billing_type === filter))
    .filter(r => !search || r.student_name.toLowerCase().includes(search.toLowerCase()));

  const setType = async (row, t) => {
    if (t === row.billing_type) return;
    const label = t === 'monthly' ? 'Monthly (billed after, for hours used)' : 'Recurring (billed before, for the next hours)';
    if (!window.confirm(`Change ${row.student_name} to ${label}?`)) return;
    try {
      await API.patch(`/bills/student/${row.student_id}/type`, { billing_type: t });
      setNotice(`✓ ${row.student_name} is now on ${t} billing`);
      load();
    } catch (e) { setNotice(errMsg(e, 'Could not change the billing type')); }
  };

  const chip = (key, label) => (
    <button key={key} onClick={() => setFilter(key)} style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
      border: `1.5px solid ${filter === key ? '#2554b8' : '#e2e8f0'}`, background: filter === key ? '#2554b8' : '#fff', color: filter === key ? '#fff' : '#475569' }}>
      {label} ({counts[key]})
    </button>
  );

  return (
    <div className="card">
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        Student Hours
        <span style={pill('#eff6ff', '#1e40af')}>{search || filter !== 'all' ? `Showing ${shown.length} of ${all.length} students` : `${all.length} students`}</span>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
        <b>Monthly</b>: billed after, for hours used. <b>Recurring</b>: billed before, for the next hours. Inactive students are hidden.
      </p>
      <Notice text={notice || err} onClose={() => { setNotice(''); setErr(''); }} />
      {popupOn !== null && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '10px 14px', borderRadius: 10, marginBottom: 12,
          background: popupOn ? '#f0fdf4' : '#f8fafc', border: `1px solid ${popupOn ? '#bbf7d0' : '#e2e8f0'}` }}>
          <div style={{ fontSize: 13 }}>
            <b>Fee reminder popup for parents: {popupOn ? 'ON' : 'OFF'}</b>
            <div className="text-muted text-small">{popupOn ? 'Shown when a bill is unpaid or partly paid, except students muted below.' : 'No parent sees the popup. Bills and pending payments still show in their app.'}</div>
          </div>
          <button className={`btn btn-sm ${popupOn ? 'btn-ghost' : 'btn-primary'}`} style={{ fontSize: 12 }} onClick={togglePopupAll}>
            {popupOn ? '🔕 Turn off for all' : '🔔 Turn on'}
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <input type="text" placeholder="Search student…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, width: '100%', maxWidth: 240, outline: 'none' }} />
        {chip('all', 'All')}{chip('to_bill', '🧾 To bill')}{chip('pending', '💳 Payment pending')}{chip('monthly', 'Monthly')}{chip('recurring', 'Recurring')}
      </div>
      {rows === null ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
        : shown.length === 0 ? <div className="empty-state"><div className="empty-icon">⏱️</div><p>No students match.</p></div>
        : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Student</th><th>Billing</th><th>Hours</th><th>Payment</th><th>Actions</th></tr></thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.student_id}>
                  <td><strong>{r.student_name}</strong><div className="text-muted text-small">{r.class_name || '—'}{r.student_status === 'paused' ? ' · Paused' : ''}</div></td>
                  <td>
                    <select value={r.billing_type} onChange={e => setType(r, e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, fontWeight: 700, background: r.billing_type === 'monthly' ? '#eff6ff' : '#f5f3ff', color: r.billing_type === 'monthly' ? '#1e40af' : '#6d28d9' }}>
                      <option value="monthly">Monthly</option>
                      <option value="recurring">Recurring</option>
                    </select>
                  </td>
                  <td style={{ minWidth: 190 }}><HoursCell r={r} /></td>
                  <td>
                    {r.pending_hours > 0
                      ? <span style={pill('#fef3c7', '#92400e')}>{fmtH(r.pending_hours)} pending · {r.unpaid_bills} bill{r.unpaid_bills === 1 ? '' : 's'}</span>
                      : r.bills_count > 0 ? <span style={pill('#dcfce7', '#15803d')}>All paid</span> : <span className="text-muted text-small">No bills yet</span>}
                    {/* Per-student fee popup toggle — kept in this column so it's always on screen */}
                    <div style={{ marginTop: 6 }}>
                      <button onClick={() => togglePopupOne(r)}
                        title={r.popup_muted ? 'Show the fee popup to this parent again' : 'Stop showing the fee popup to this parent'}
                        style={{ padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                          border: `1.5px solid ${r.popup_muted ? '#16a34a' : '#d97706'}`, background: '#fff', color: r.popup_muted ? '#16a34a' : '#d97706' }}>
                        {r.popup_muted ? '🔔 Unmute payment popup' : '🔕 Mute payment popup'}
                      </button>
                      {r.popup_muted && <div className="text-small" style={{ color: '#94a3b8', marginTop: 3 }}>Popup is muted for this parent</div>}
                    </div>
                  </td>
                  <td style={{ maxWidth: 240 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className={`btn btn-sm ${toBill(r) ? 'btn-primary' : 'btn-ghost'}`} style={{ fontSize: 11 }} onClick={() => setGenFor(r)}>
                        🧾 {r.billing_type === 'monthly' ? 'Generate bill' : r.needs_first_bill ? 'First bill' : 'Bill next hours'}
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setDetailFor(r)}>📂 Bills</button>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#2563eb' }} onClick={() => setRemindFor(r)}>📲 Remind</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {genFor && <GenerateBillModal row={genFor} onClose={() => setGenFor(null)} onDone={t => { setGenFor(null); setNotice(t); load(); }} />}
      {detailFor && <StudentBillsModal studentId={detailFor.student_id} studentName={detailFor.student_name} onClose={() => setDetailFor(null)} onChanged={load} />}
      {remindFor && <ReminderModal row={remindFor} onClose={() => setRemindFor(null)} />}
    </div>
  );
}

function HoursCell({ r }) {
  if (r.billing_type === 'monthly') {
    return (
      <div style={{ fontSize: 13 }}>
        <div>This month: <b>{fmtH(r.this_month_hours)}</b></div>
        <div className="text-muted text-small">Not billed: {fmtH(r.unbilled_hours)} since {fmtDate(r.next_bill_from)}</div>
        {r.ready_to_bill_month && <span style={{ ...pill('#fee2e2', '#b91c1c'), marginTop: 4 }}>Bill {monthName(r.ready_to_bill_month)}</span>}
      </div>
    );
  }
  if (r.needs_first_bill) return <span style={pill('#f1f5f9', '#475569')}>No bill yet</span>;
  if (r.ready_for_next_bill) {
    return (
      <div style={{ fontSize: 13 }}>
        <span style={pill('#fee2e2', '#b91c1c')}>All paid hours used</span>
        {r.over_hours > 0 && <div className="text-small" style={{ color: '#b91c1c', marginTop: 3 }}>{fmtH(r.over_hours)} used beyond — bill next hours</div>}
      </div>
    );
  }
  const c = r.current_bill;
  const pct = Math.min(100, Math.round((c.used / c.hours) * 100));
  const color = pct >= 90 ? '#dc2626' : pct >= 75 ? '#f59e0b' : '#16a34a';
  return (
    <div style={{ fontSize: 13 }}>
      <div><b style={{ color }}>{fmtH(c.used)}</b> of {fmtH(c.hours)} · <b>{fmtH(c.remaining)} left</b></div>
      <div style={{ background: '#e2e8f0', borderRadius: 20, height: 6, overflow: 'hidden', marginTop: 4 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color }} />
      </div>
    </div>
  );
}

// ── Tab: Bills (every bill, newest first) ───────────────────────────────────
export function BillsList() {
  const [bills, setBills] = useState(null);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('open');
  const [search, setSearch] = useState('');
  const [detailFor, setDetailFor] = useState(null);

  const load = useCallback(() => {
    API.get('/bills/').then(r => { setBills(r.data || []); setErr(''); }).catch(e => setErr(errMsg(e, 'Could not load bills')));
  }, []);
  useEffect(() => { load(); }, [load]);
  const { buttons, modals, notice, setNotice } = useBillActions(load);

  const all = bills || [];
  const match = b => status === 'all' || (status === 'open' ? b.pay_status !== 'paid' : b.pay_status === status);
  const shown = all.filter(match).filter(b => !search || (b.student_name || '').toLowerCase().includes(search.toLowerCase()));
  const pendingTotal = all.reduce((s, b) => s + (b.pending_hours || 0), 0);
  const chip = (key, label, n) => (
    <button key={key} onClick={() => setStatus(key)} style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
      border: `1.5px solid ${status === key ? '#2554b8' : '#e2e8f0'}`, background: status === key ? '#2554b8' : '#fff', color: status === key ? '#fff' : '#475569' }}>
      {label} ({n})
    </button>
  );

  return (
    <div className="card">
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        🧾 Bills
        {pendingTotal > 0 && <span style={pill('#fef3c7', '#92400e')}>{fmtH(pendingTotal)} payment pending in total</span>}
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
        Create bills from the <b>Student Hours</b> tab. Record payments (full or partial), revise or cancel bills here.
      </p>
      <Notice text={notice || err} onClose={() => { setNotice(''); setErr(''); }} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <input type="text" placeholder="Search student…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, width: '100%', maxWidth: 240, outline: 'none' }} />
        {chip('open', 'Not fully paid', all.filter(b => b.pay_status !== 'paid').length)}
        {chip('unpaid', 'Unpaid', all.filter(b => b.pay_status === 'unpaid').length)}
        {chip('partial', 'Partial', all.filter(b => b.pay_status === 'partial').length)}
        {chip('paid', 'Paid', all.filter(b => b.pay_status === 'paid').length)}
        {chip('all', 'All', all.length)}
      </div>
      {bills === null ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
        : shown.length === 0 ? <div className="empty-state"><div className="empty-icon">🧾</div><p>No bills here.</p></div>
        : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Student</th><th>Bill</th><th>Hours</th><th>Paid</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {shown.map(b => (
                <tr key={b.id}>
                  <td><strong>{b.student_name}</strong><div className="text-muted text-small">{b.class_name || '—'}</div></td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{b.label}</div>
                    <div className="text-muted text-small">{b.billing_type === 'monthly' ? 'Monthly' : 'Recurring'}{b.invoice_number ? ` · ${b.invoice_number}` : ''}{b.revision > 1 ? ` · revised (v${b.revision})` : ''}</div>
                  </td>
                  <td style={{ fontWeight: 700 }}>{fmtH(b.hours)}</td>
                  <td className="text-small">{b.payments.length ? <>{rupees(b.paid_amount)}<div className="text-muted">{fmtH(b.paid_hours)}</div></> : '—'}</td>
                  <td><PayBadge bill={b} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {buttons(b, b.student_name)}
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setDetailFor(b)}>📂 All bills</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modals}
      {detailFor && <StudentBillsModal studentId={detailFor.student_id} studentName={detailFor.student_name} onClose={() => setDetailFor(null)} onChanged={load} />}
    </div>
  );
}
